import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectIsAddModalOpen,
  selectUnderCarriageForm,
  selectUnderCarriageSaving,
} from "../../redux/selector";
import { underCarriageActions } from "../../redux/slice";
import { UnderCarriageForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof UnderCarriageForm;
  label: string;
};

const FIELDS: FieldConfig[] = [
  { key: "code", label: "CODE" },
  { key: "description", label: "DESCRIPTION" },
  { key: "height", label: "HEIGHT" },
];

const P = "UNDER_CARRIAGE.MODAL";

const AddUnderCarriageModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectUnderCarriageForm);
  const saving = useSelector(selectUnderCarriageSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(underCarriageActions.closeAddModal());
  };

  const setField = (name: keyof UnderCarriageForm, value: string) =>
    dispatch(underCarriageActions.setFormField({ name, value }));

  const isValid =
    Boolean(form.code.trim()) &&
    Boolean(form.description.trim()) &&
    Boolean(form.height.trim());

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${P}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                variant="outlined"
                placeholder={t(`${P}.PLACEHOLDERS.ENTER_${label}`)}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                onChange={(event) => setField(key, event.target.value)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(underCarriageActions.addUnderCarriage())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddUnderCarriageModal;
